import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PositionData,
  PositionProtectionData,
  SetPositionProtectionResponse,
} from "@crypto-trading/common/browser";
import { ApiClientManager } from "@/utils/api-client-factory";
import { getTradeErrorMessage } from "@/utils/trade-error";
import {
  canManageProtection,
  toPositivePrice,
} from "@/lib/position-protection";

export const positionProtectionKey = (userId: string, positionId: string) =>
  ["positions", "protection", userId, positionId] as const;

/** Leave a field out to keep it, null to remove, a price to set or replace. */
export interface ProtectionChange {
  takeProfitPrice?: number | null;
  stopLossPrice?: number | null;
}

export interface ProtectionTrailing {
  activationPercent: number | null;
  distancePercent: number | null;
  peakPrice: number | null;
}

export interface PositionProtectionState {
  /** The position's TP/SL can be read and changed on the exchange. */
  editable: boolean;
  /** Live read from the exchange is in flight (list values shown meanwhile). */
  isLoading: boolean;
  /** Live read failed; the list values are shown. */
  readFailed: boolean;
  /** Values come from the exchange (not only the list). */
  isLive: boolean;
  takeProfitPrice: number | null;
  stopLossPrice: number | null;
  trailing: ProtectionTrailing | null;
  takeProfitOrderCount: number;
  stopLossOrderCount: number;
  save: (change: ProtectionChange) => Promise<SetPositionProtectionResponse>;
  isSaving: boolean;
}

/** User-facing text for a failed TP/SL change, as the api sent it. */
export const protectionErrorMessage = (error: unknown) =>
  getTradeErrorMessage(error, "Could not change the TP/SL. Nothing was changed.");

/**
 * TP/SL of an open position: the live orders on the exchange (read when
 * Position Details opens), falling back to the values last set in Trigger
 * that come with the open-positions list.
 */
export function usePositionProtection({
  userId,
  position,
  enabled,
}: {
  userId: string;
  position: PositionData | null;
  enabled: boolean;
}): PositionProtectionState {
  const queryClient = useQueryClient();
  const positionId = position?.positionId ?? "";
  const editable = position ? canManageProtection(position) : false;
  const queryKey = positionProtectionKey(userId, positionId);

  const query = useQuery<PositionProtectionData>({
    queryKey,
    queryFn: () =>
      ApiClientManager.getPositionClient().getPositionProtection({
        userId,
        positionId,
      }),
    enabled: enabled && editable && Boolean(userId),
    staleTime: 15_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation<
    SetPositionProtectionResponse,
    Error,
    ProtectionChange
  >({
    mutationFn: (change) =>
      ApiClientManager.getPositionClient().setPositionProtection({
        userId,
        positionId,
        ...change,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response.protection);
      void queryClient.invalidateQueries({
        queryKey: ["positions", "open"],
        exact: false,
      });
    },
  });

  const live = query.data;
  const trailingFromList: ProtectionTrailing | null = position?.trailingTakeProfit
    ? {
        activationPercent: position.trailingActivationPercent ?? null,
        distancePercent: position.trailingDistancePercent ?? null,
        peakPrice: toPositivePrice(position.trailingPeakPrice),
      }
    : null;
  const trailing: ProtectionTrailing | null = live
    ? live.trailing
      ? {
          activationPercent: live.trailing.activationPercent ?? null,
          distancePercent: live.trailing.distancePercent ?? null,
          peakPrice: toPositivePrice(live.trailing.peakPrice),
        }
      : null
    : trailingFromList;
  const orders = live?.orders ?? [];

  return {
    editable,
    isLoading: editable && enabled && query.isLoading,
    readFailed: editable && query.isError && !live,
    isLive: Boolean(live),
    takeProfitPrice: live
      ? toPositivePrice(live.takeProfit?.triggerPrice)
      : toPositivePrice(position?.takeProfitPrice),
    stopLossPrice: live
      ? toPositivePrice(live.stopLoss?.triggerPrice)
      : toPositivePrice(position?.stopLossPrice),
    trailing,
    takeProfitOrderCount: orders.filter((order) => order.kind === "takeProfit").length,
    stopLossOrderCount: orders.filter((order) => order.kind === "stopLoss").length,
    save: (change) => mutation.mutateAsync(change),
    isSaving: mutation.isPending,
  };
}
